import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useGenerationHistory } from '../hooks/useGenerationHistory';
import { useDownloadGeneration } from '../hooks/useDownloadGeneration';
import type { ApiError, GenerationStatus } from '../types/reportBuilder';
import type { StatusTone } from '@ui-kit';

const DEFAULT_PAGE_SIZE = 20;

function genStatusTone(s: GenerationStatus): StatusTone {
  return s === 'SUCCESS' ? 'success' : 'danger';
}

function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${45 + (i * 17) % 45}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * GenerationHistoryPage — 생성 이력 목록 (/report-builder/history)
 * 5상태: loading / empty / error / default / dialog(없음 — 다운로드는 즉시 트리거)
 */
export function GenerationHistoryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [formatFilter, setFormatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerScope, setOwnerScope] = useState<'mine' | 'all'>('mine');
  const [page, setPage] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState({
    format: '',
    status: '',
    ownerScope: 'mine' as 'mine' | 'all',
    page: 0,
  });

  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const historyQuery = useGenerationHistory({
    format: (appliedFilters.format as 'EXCEL' | 'PDF') || undefined,
    status: (appliedFilters.status as GenerationStatus) || undefined,
    ownerScope: appliedFilters.ownerScope,
    page: appliedFilters.page,
    size: DEFAULT_PAGE_SIZE,
    sort: 'generatedAt,desc',
  });

  const { doDownload, getStatus } = useDownloadGeneration();

  const pageData = historyQuery.data;
  const isLoading = historyQuery.isLoading;
  const isError = historyQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? false);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({ format: formatFilter, status: statusFilter, ownerScope, page: 0 });
  }, [formatFilter, statusFilter, ownerScope]);

  const handleReset = useCallback(() => {
    setFormatFilter('');
    setStatusFilter('');
    setOwnerScope('mine');
    setPage(0);
    setAppliedFilters({ format: '', status: '', ownerScope: 'mine', page: 0 });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleDownload = useCallback(
    async (genId: number) => {
      showToast(t('reportBuilder.toast.downloadStarted'));
      await doDownload(genId);
      const s = getStatus(genId);
      if (s === 'error') {
        showToast(t('reportBuilder.toast.downloadFailed'));
      } else {
        showToast(t('reportBuilder.toast.downloadComplete'));
      }
    },
    [doDownload, getStatus, showToast, t]
  );

  const TABLE_HEADERS = useMemo(
    () => [
      t('reportBuilder.history.column.generatedAt'),
      t('reportBuilder.history.column.definitionName'),
      t('reportBuilder.history.column.format'),
      t('reportBuilder.history.column.rowCount'),
      t('reportBuilder.history.column.barcodeCount'),
      t('reportBuilder.history.column.status'),
      t('reportBuilder.history.column.actions'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('reportBuilder.history.title')}
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/report-builder')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-brand-primary)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                padding: 0,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('reportBuilder.list.title')}
            </button>
            {' > '}
            {t('reportBuilder.history.breadcrumb')}
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/report-builder')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.history.button.backToList')}
          </button>
        }
      />

      {/* 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {pageData.totalElements > 0
            ? t('reportBuilder.history.totalCount', { count: pageData.totalElements })
            : t('reportBuilder.list.noResults')}
        </p>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('reportBuilder.history.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 포맷 */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('reportBuilder.history.column.format')}
          <select
            value={formatFilter}
            onChange={(e) => setFormatFilter(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('reportBuilder.history.filter.formatAll')}</option>
            <option value="EXCEL">{t('reportBuilder.format.EXCEL')}</option>
            <option value="PDF">{t('reportBuilder.format.PDF')}</option>
          </select>
        </label>

        {/* 상태 */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('reportBuilder.history.column.status')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('reportBuilder.history.filter.statusAll')}</option>
            <option value="SUCCESS">{t('reportBuilder.result.SUCCESS')}</option>
            <option value="FAILED">{t('reportBuilder.result.FAILED')}</option>
          </select>
        </label>

        {/* 범위 */}
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <legend style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
            {t('reportBuilder.list.filter.scopeMine')}
          </legend>
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input
                type="radio"
                name="historyOwnerScope"
                value="mine"
                checked={ownerScope === 'mine'}
                onChange={() => setOwnerScope('mine')}
              />
              {t('reportBuilder.history.filter.scopeMine')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input
                type="radio"
                name="historyOwnerScope"
                value="all"
                checked={ownerScope === 'all'}
                onChange={() => setOwnerScope('all')}
              />
              {t('reportBuilder.history.filter.scopeAll')}
            </label>
          </div>
        </fieldset>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.history.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.history.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('reportBuilder.history.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('reportBuilder.history.error.loadFailed')}
              message={
                (historyQuery.error as unknown as ApiError)?.message ??
                t('reportBuilder.error.network')
              }
              onRetry={() => void historyQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('reportBuilder.history.empty.title')}
              description={t('reportBuilder.history.empty.description')}
              action={
                <button
                  type="button"
                  onClick={() => navigate('/report-builder')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('reportBuilder.history.button.backToList')}
                </button>
              }
            />
          </div>
        )}

        {/* default */}
        {showTable && pageData && (
          <>
            <div
              role="region"
              aria-label={t('reportBuilder.history.tableAriaLabel')}
              style={{ overflowX: 'auto' }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('reportBuilder.history.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => {
                    const dlStatus = getStatus(row.id);
                    const isDownloading = dlStatus === 'loading';
                    return (
                      <tr
                        key={row.id}
                        style={{ transition: 'background 0.1s' }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            'var(--color-surface-muted)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = '';
                        }}
                      >
                        {/* 생성 시각 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDateTime(row.generatedAt, i18n.language)}
                        </td>
                        {/* 정의명 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {row.definitionName}
                        </td>
                        {/* 포맷 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {t(`reportBuilder.format.${row.format}`, { defaultValue: row.format })}
                        </td>
                        {/* 행수 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            textAlign: 'right',
                          }}
                        >
                          {row.rowCount?.toLocaleString() ?? '-'}
                        </td>
                        {/* 바코드 수 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            textAlign: 'right',
                          }}
                        >
                          {row.barcodeCount ?? 0}
                        </td>
                        {/* 상태 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                          }}
                        >
                          <StatusPill tone={genStatusTone(row.status)}>
                            {t(`reportBuilder.result.${row.status}`)}
                          </StatusPill>
                        </td>
                        {/* 액션 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.status === 'SUCCESS' ? (
                            <button
                              type="button"
                              disabled={isDownloading}
                              onClick={() => void handleDownload(row.id)}
                              style={{
                                padding: 'var(--spacing-1) var(--spacing-2)',
                                background: isDownloading ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                                color: isDownloading ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600,
                                cursor: isDownloading ? 'not-allowed' : 'pointer',
                                fontFamily: 'var(--font-family-sans)',
                              }}
                            >
                              {isDownloading ? '...' : t('reportBuilder.history.button.download')}
                            </button>
                          ) : (
                            <span
                              style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-danger)',
                              }}
                            >
                              {row.errorCode ?? t('reportBuilder.result.FAILED')}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('reportBuilder.history.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav
                aria-label={t('reportBuilder.history.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('reportBuilder.history.pagination.prev')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('reportBuilder.history.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('reportBuilder.history.pagination.next')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}
